import { Button, Col, Form, Input, Modal, Row, Spin, Tag, Typography } from "antd";
import { t } from "i18next";
import useModalUpdateVatInfo, { VAT_FIELD } from "./useModalUpdateVatInfo";
import "./index.scss";

/** Định dạng MST: 10 số, hoặc 10 số + "-" + 3 số (VD 0302525162-001) */
const TAX_CODE_PATTERN = /^\d{10}(-\d{3})?$/;

/**
 * Pop up "Thông tin xuất hoá đơn VAT".
 * Mở từ icon [Chỉnh sửa] ở khối Xuất hóa đơn VAT của màn Chi tiết đơn hàng.
 */
const ModalUpdateVatInfo = ({ visible, onClose, order, onRefetch }) => {
  const { form, loading, submitting, setFormValues, normalizeTaxCode, getSuggestions, onSelectSuggestion, handleSave } =
    useModalUpdateVatInfo({ order, visible, onClose, onRefetch });

  // Khớp mockup: dấu * bắt buộc nằm SAU tên field thay vì trước (mặc định AntD tự thêm `::before`
  // khi field có rules required) — cùng cách xử lý với pop up Thay đổi PTVC.
  const renderRequiredLabel = (text) => (
    <>
      {text}
      <span style={{ color: "#ff4d4f" }}>&nbsp;*</span>
    </>
  );

  // Hàng chip gợi ý, đặt vào slot `extra` của Form.Item để không phải tự canh lề. Không có gợi ý
  // thì trả `null` — antd không render `.ant-form-item-extra`, không chừa khoảng trắng.
  const renderSuggestions = (name) => {
    const options = getSuggestions(name);
    if (!options?.length) return null;

    // Xếp dọc, mỗi chip 1 dòng như mockup. `alignItems: flex-start` để chip co theo nội dung —
    // flex column mặc định `stretch` sẽ kéo chip rộng hết ô nhập. `marginTop` là khoảng cách từ
    // đáy ô nhập xuống chip đầu (`.ant-form-item-extra` không có padding riêng).
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, marginTop: 8 }}>
        {options.map((value) => (
          <Tag
            key={value}
            onClick={() => onSelectSuggestion(name, value)}
            style={{
              margin: 0,
              cursor: "pointer",
              background: "#F3F4F6",
              border: "none",
              color: "#6B7280",
              borderRadius: 4,
            }}
          >
            {value}
          </Tag>
        ))}
      </div>
    );
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      width={520}
      centered
      destroyOnClose
      className="modal-update-vat-info"
      title={
        <Typography.Text strong style={{ fontSize: 16, display: "block", textAlign: "center" }}>
          {t("v2.order.detail.modalUpdateVatInfo.title")}
        </Typography.Text>
      }
      footer={
        <Row justify="center" gutter={12}>
          <Col>
            <Button onClick={onClose} style={{ minWidth: 120 }}>
              {t("v2.order.detail.modalUpdateVatInfo.cancel")}
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={handleSave}
              loading={submitting}
              disabled={loading}
              style={{ minWidth: 120 }}
            >
              {t("v2.order.detail.modalUpdateVatInfo.save")}
            </Button>
          </Col>
        </Row>
      }
    >
      {/* Chặn nhập trong lúc chờ `getVatInfo`: `.ant-spin-blur` set `pointer-events: none` nên
          không xảy ra cảnh user gõ xong thì `current` về ghi đè mất chữ vừa gõ. */}
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onValuesChange={(_, all) => setFormValues(all)}>
          <Form.Item
            label={renderRequiredLabel(t("v2.order.detail.modalUpdateVatInfo.taxCodeLabel"))}
            name={VAT_FIELD.TAX_ID_NUMBER}
            required={false}
            // Lọc ký tự lạ + tự chèn dấu gạch ngay khi giá trị vào form store — phủ cả gõ tay và
            // paste bằng chuột, khác `onKeyDown` chỉ chặn được lúc gõ. Đã chặn độ dài nên
            // `Input` không cần `maxLength`.
            normalize={normalizeTaxCode}
            rules={[
              { required: true, message: t("v2.order.detail.modalUpdateVatInfo.blankTaxCode") },
              { pattern: TAX_CODE_PATTERN, message: t("v2.order.detail.modalUpdateVatInfo.invalidTaxCode") },
            ]}
            extra={renderSuggestions(VAT_FIELD.TAX_ID_NUMBER)}
          >
            <Input placeholder={t("v2.order.detail.modalUpdateVatInfo.taxCodePlaceholder")} />
          </Form.Item>

          <Form.Item
            label={renderRequiredLabel(t("v2.order.detail.modalUpdateVatInfo.companyNameLabel"))}
            name={VAT_FIELD.COMPANY_NAME}
            required={false}
            rules={[{ required: true, message: t("v2.order.detail.modalUpdateVatInfo.blankCompanyName") }]}
            extra={renderSuggestions(VAT_FIELD.COMPANY_NAME)}
          >
            <Input placeholder={t("v2.order.detail.modalUpdateVatInfo.companyNamePlaceholder")} maxLength={255} />
          </Form.Item>

          <Form.Item
            label={renderRequiredLabel(t("v2.order.detail.modalUpdateVatInfo.emailLabel"))}
            name={VAT_FIELD.EMAIL}
            required={false}
            rules={[
              { required: true, message: t("v2.order.detail.modalUpdateVatInfo.blankEmail") },
              { type: "email", message: t("v2.order.detail.modalUpdateVatInfo.invalidEmail") },
            ]}
            extra={renderSuggestions(VAT_FIELD.EMAIL)}
          >
            <Input placeholder={t("v2.order.detail.modalUpdateVatInfo.emailPlaceholder")} maxLength={100} />
          </Form.Item>

          <Form.Item
            label={renderRequiredLabel(t("v2.order.detail.modalUpdateVatInfo.addressLabel"))}
            name={VAT_FIELD.ADDRESS}
            required={false}
            rules={[{ required: true, message: t("v2.order.detail.modalUpdateVatInfo.blankAddress") }]}
            extra={renderSuggestions(VAT_FIELD.ADDRESS)}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder={t("v2.order.detail.modalUpdateVatInfo.addressPlaceholder")} maxLength={255} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ModalUpdateVatInfo;
